import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { FilterUtils, PaginationUtils } from '@ocean/utils'
import { Card } from 'types/card'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getSurveys } from 'modules/trainingLibrary/Surveys/api/surveysService'
import { SurveysFilterItem, SurveysFilterItems } from 'modules/trainingLibrary/Surveys/types'
import { SurveysState, ApiSurveysResponse } from './types'

const initialState: SurveysState = {
  surveysData: {},
  surveysFilters: {
    isCompleted: { keyType: FilterKeyType.Boolean, searchKey: 'result.isCompleted' },
    categoryId: { searchKey: 'typeSpecificValues.categoryId' },
  },
}

const surveysSlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {
    surveysDataLoaded(state, action: PayloadAction<ApiMiddlewarePayload<ApiSurveysResponse>>) {
      state.surveysData = action.payload
    },
    setSurveysFilters(state, action: PayloadAction<SurveysFilterItem>) {
      const filter = state.surveysFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { surveysDataLoaded, setSurveysFilters } = surveysSlice.actions

export { setSurveysFilters }
export default surveysSlice.reducer

export const getFilteredSurveys = createSelector(
  (surveysCards: Card[] = [], filters: SurveysFilterItems) => ({ surveysCards, filters }),
  ({ surveysCards, filters }) => FilterUtils.filterData(filters, surveysCards)
)

export const getSurveysPageItems = createSelector(
  (
    surveysCards: Card[] = [],
    filters: SurveysFilterItems,
    currentPageId: number,
    currentPageSize: number
  ) => ({
    filteredSurveysCards: getFilteredSurveys(surveysCards, filters),
    currentPageId,
    currentPageSize,
  }),
  ({ filteredSurveysCards, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<Card>(filteredSurveysCards, currentPageId, currentPageSize)
)

export const loadSurveysData = () => {
  return createApiAction({
    apiCalls: {
      surveys: getSurveys,
    },
    payloadAction: surveysDataLoaded,
  })
}
